import { Component, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogContentBase, DialogRef, DialogResult } from '@progress/kendo-angular-dialog';
import { tap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { UiNotificationService } from 'src/app/external-ui-shared/services/ui-notification.service';
import { PortalMessageTemplateDictionaryDataItem } from 'src/app/feature-module/message/models/PortalMessageTemplateDictionaryDataItem';
import { CustomDialogSettings } from 'src/app/shared/model/CustomDialogSettings';
import { CustomDialogService } from 'src/app/shared/services/custom-dialog.service';
import { FormUtilsService } from 'src/app/shared/services/form-utils.service';
import { PortalMessage, PortalMessageFormBuilder } from '../../models/PortalMessage';
import { PortalMessageRecipient, PortalMessageRecipientFormBuilder } from '../../models/PortalMessageRecipient';
import { RecipientRole } from '../../models/RecipientRole';
import { SendingChannelType } from '../../models/SendingChannelType';
import { PortalMessageService } from '../../service/portal-message.service';
import { PortalMessageTemplatePickerComponent } from '../portal-message-template-picker/portal-message-template-picker.component';

@Component({
  selector: 'app-compose-sms-message',
  templateUrl: './compose-sms-message.component.html',
  styleUrls: ['./compose-sms-message.component.scss']
})
export class ComposeSmsMessageComponent extends DialogContentBase implements OnInit {

  constructor(
    public dialog: DialogRef, 
    private fb: UntypedFormBuilder,
      private authService: AuthService,
      private formUtilsService: FormUtilsService,
      private uiNotification: UiNotificationService,
      private messageService: PortalMessageService,
      private translateService: TranslateService,
      private customDialogService: CustomDialogService) { 
        super(dialog);
      }

  @Input() claimIdentifier:string;
  @Input() threadId:string;
  composeMessageForm: UntypedFormGroup;
  addRecipientForm: UntypedFormGroup;
  message:PortalMessage;
  sendingChannelType = SendingChannelType.Sms;
  proposedRecipients:PortalMessageRecipient[];
  contactRecipientHasData:boolean;
  loadingRecipientData:boolean;

  allowedRecipientRoles = RecipientRole.RolesForSms;

  
  @ViewChild("composeSmsDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;

  ngOnInit(): void {
    this.message = PortalMessageFormBuilder.initPortalMessage(this.sendingChannelType, this.threadId);
    this.message.claimIdentifier = this.claimIdentifier
    this.composeMessageForm = PortalMessageFormBuilder.getForm(this.fb, this.message);
    this.addRecipientForm = PortalMessageRecipientFormBuilder.getForm(
      this.fb, 
      PortalMessageRecipientFormBuilder.initPortalMessageRecipient(),this.sendingChannelType);
  }

  send(){

      if(!this.formUtilsService.markFormGroupTouched(this.composeMessageForm)){
        this.uiNotification.showValidationError();
        return;
      }
  
      Object.assign(this.message, this.composeMessageForm.value);

      if(this.message.recipients?.length == 0){
        this.uiNotification.showWarning("validations.defineAtLeastOneRecipient");
        return;
      }

      const task = this.uiNotification.showRunningOperation('operations.sendingOngoing');

      this.messageService.addMessage(this.message).subscribe(
        () => {
          task.hide();
          this.uiNotification.showSuccess('validations.smsSendSuccesfully');
          this.dialog.close({ text: 'OK' });
        }
      );

    }

    chooseTemplate(){

      const dialogSettings: CustomDialogSettings = {
        content: PortalMessageTemplatePickerComponent,
        appendTo: this.containerRef,
        title: this.translateService.instant('claims-center.claimMessages.chooseTemplate'),
        minWidth:'30vw',
        minHeight:'30vh',
        maxWidth:'70vw',
        maxHeight:'70vh',
        dialogInputs: {
          sendingChannelTypeId : this.sendingChannelType,
          claimIdentifier : this.claimIdentifier
        },
        resultCallback: (r: DialogResult) => {
          if(r['template']) {
            const choosenTemplate = r['template'] as PortalMessageTemplateDictionaryDataItem;
            this.message.contentText = choosenTemplate.templateContent;
            this.message.title = choosenTemplate.title;
            this.message.templateId = choosenTemplate.id;
           this.composeMessageForm = PortalMessageFormBuilder.getForm(this.fb, this.message);
           }
        }
      };
  
      this.customDialogService.showDialog(dialogSettings);

    }

    addRecipient(){

      if(!this.formUtilsService.markFormGroupTouched(this.addRecipientForm)){
        this.uiNotification.showValidationError();
        return;
      }

      const addRecipientData = PortalMessageRecipientFormBuilder.initPortalMessageRecipient();
      Object.assign(addRecipientData, this.addRecipientForm.value);

      if(this.isRecipientPhoneDuplicate(addRecipientData.recipientContact)){
        this.uiNotification.showWarning('claims-center.claimMessages.sameRecipientMessageSms');
        return;
      }

      this.composeMessageForm.controls.recipients.value.push(addRecipientData);
    }

    removeRecipient(recipient: PortalMessageRecipient){
      const index = this.composeMessageForm.controls.recipients.value.indexOf(recipient, 0);
      this.composeMessageForm.controls.recipients.value.splice(index,1);
    }

    recipientRoleChanged(roleId:number){
      this.loadingRecipientData = true;
      this.messageService.getRecipientData(this.claimIdentifier, roleId)
      .pipe( tap(() =>  this.loadingRecipientData = false))
      .subscribe(
        (r) =>  {
          
          if(!r.info && roleId != RecipientRole.Other){
            this.contactRecipientHasData = false;
            return;
          }
          
        const recipient = PortalMessageRecipientFormBuilder.initPortalMessageRecipient();
        recipient.recipientContact = r.phoneNumber;

        if(this.isRecipientPhoneDuplicate(recipient.recipientContact)){
          this.uiNotification.showWarning('claims-center.claimMessages.sameRecipientMessageSms');
          this.contactRecipientHasData = false;
          return;
        }
        recipient.recipientInfo = r.info;
        recipient.recipientRole = roleId;
        recipient.isDisabledPortalUser = r.isDisabledPortalUser;
        this.addRecipientForm = PortalMessageRecipientFormBuilder.getForm(
          this.fb, recipient,this.sendingChannelType);
          this.contactRecipientHasData = true;
        }
      );

    }

    private isRecipientPhoneDuplicate(phone: string): boolean{
      if(!phone) return false;

      const normalized = phone.replace(/\D/g, '');
      if(!this.composeMessageForm || !this.composeMessageForm.controls || !this.composeMessageForm.controls.recipients) return false;
      const recipients = this.composeMessageForm.controls.recipients.value as PortalMessageRecipient[];
      if(!recipients || recipients.length === 0) return false;
      return recipients.some(r => r && r.recipientContact && r.recipientContact.replace(/\D/g, '') === normalized);
    }
  
    charsCount(){
      return this.composeMessageForm.controls.contentText.value.length + this.composeMessageForm.controls.title.value.length;
    }
}

